import React, {useMemo, useEffect, useState, useRef} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useTable, usePagination, useFlexLayout, Column} from 'react-table';

import {ReactComponent as LeftIcon} from 'assets/svg/arrow-left.svg';
import {ReactComponent as RightIcon} from 'assets/svg/arrow-right.svg';
import {ReactComponent as ShowInfo} from 'assets/svg/edit2.svg';
import {
    isStandartCategory,
    VAT_CATEGORY,
} from 'components/pages/DocumentsCreate/InvoiceNew/helpers/consts';

import {Button, ButtonVariant} from 'components/ui-kit/Button';

import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {Input} from 'components/ui-kit/Input';
import {Select} from 'components/ui-kit/Select';
import {Tooltip} from 'components/ui-kit/Tooltip';
import {$doesInvoiceExist} from 'models/documents/invoices/common/init';
import {
    $invoiceLinesHolder,
    removeInvoiceLinesHolder,
    copyInvoiceLineData,
} from 'models/documents/invoices/create';

import {
    setInlineModalFormReadonly,
    setModalDuplicateLinesModal,
    setModalGroupByGtinSettings,
} from 'models/documents/invoices/details';

import {colors} from 'theme';

import {ModalHandler} from '../../ModalHandler';
import {LineControlComponent} from '../LineControl';

import {
    CellContainer,
    HeaderCell,
    Root,
    TableView,
    PaginationContainer,
    PageInfo,
    PaginationControls,
    PaginationButton,
    TextWrapper,
    RowText,
    RowTextEllipsis,
    DefaultCell,
    TextWrapperRow,
    TextWrapperItem,
    GroupByGtinButtonRoot,
} from './styled';

export interface PaginationControlsProps {
    pageIndex: number;
    pageOptions: number[];
    canPreviousPage: boolean;
    canNextPage: boolean;
    pageCount: number;
    gotoPage: (page: number) => void;
    nextPage: () => void;
    previousPage: () => void;
    setPageSize: (size: number) => void;
    pageSize: number;
}

interface SimpleTableProps {
    columns: Column<object>[];
    data: object[];
    cellContainerSettings?: string;
    tableViewExtraSettings?: Record<string, string>;
    hideHeaders?: boolean;
    onPaginationReady?: (paginationProps: PaginationControlsProps) => void;
    updateMyData: (index: number, id: string, value: string) => void;
    skipPageReset: boolean;
    editableCell?: boolean;
    initialState?: {pageIndex: number; pageSize: number};
    emptyDataComponent?: React.ReactNode;
}

const GroupByGtinButton = ({index, gtin}: {index: number; gtin?: string}) => (
    <GroupByGtinButtonRoot
        type="button"
        onClick={() => {
            if (!gtin) {
                return;
            }

            setModalGroupByGtinSettings({
                lineIndex: index,
                show: true,
                gtin,
            });
        }}
    >
        <span>View codes</span>
        <Icon source={RightIcon} width={8} height={13} />
    </GroupByGtinButtonRoot>
);

const EditableCell = ({
    value: initialValue,
    row: {index},
    column: {id},
    updateMyData,
}: {
    value: string;
    row: {index: number};
    column: {id: string};
    updateMyData: (index: number, id: string, value: string) => void;
}) => {
    const {t: translation} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines';
    const t = (key: string) => translation(`${GEN_INFO}.${key}`);
    const doesInvoiceExist = useStore($doesInvoiceExist);
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const [value, setValue] = useState(initialValue);
    const [isEditing, setIsEditing] = useState(false);
    const vatCategoryValue = id === 'vatCategory' ? invoiceLinesHolder[index]?.vatCategory : null;

    const currentItem = invoiceLinesHolder[index];
    const {isMarkedItem, copyActions, vatCategory} = currentItem;

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
    };

    const onSelectChange = (selectedOption: any) => {
        if (id === 'vatCategory' && selectedOption) {
            updateMyData(index, 'vatCategory', selectedOption);
            setIsEditing(false);
        }
    };

    const onBlur = () => {
        updateMyData(index, id, value);
        setIsEditing(false);
    };

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const additionalStyles = `
        text-align: left;
        max-width: 250px;
        border-radius: 8px;
        opacity: 1 !important;
    `;

    const checkLineInfo = (toggleModal: () => void) => (
        <div style={{cursor: 'pointer', marginLeft: 'auto'}}>
            <Icon
                source={ShowInfo}
                width={20}
                height={20}
                fillColor="#FF4B55"
                onClick={() => {
                    toggleModal();
                }}
            />
        </div>
    );

    function wrapObjectToText(item: unknown, lineIndex: number, fieldType: string) {
        if (!item) {
            return '';
        }
        const itemHolderLabel =
            typeof item === 'string' ? item : (item as {label: string; value: string}).label;

        const tooltipId = `tooltip-${lineIndex}-${fieldType}`;
        return (
            <RowText padding={'0'}>
                <RowTextEllipsis data-tip={itemHolderLabel} data-for={tooltipId}>
                    <Tooltip
                        id={tooltipId}
                        place={'bottom'}
                        backgroundColor="white"
                        textColor="#111111"
                        additionalStyles={
                            additionalStyles + 'box-shadow: 0px 0px 6px rgba(116, 116, 116, 0.3);'
                        }
                    />
                    {itemHolderLabel}
                </RowTextEllipsis>
            </RowText>
        );
    }

    const noPadding = useMemo(() => {
        if (id === 'codeGtin') {
            return '0';
        }
        if (value !== '' || id === 'vat') {
            return '0 16px';
        }
        return '0';
    }, [value, id]);

    const dataToDisaply = useMemo(() => {
        if (id === 'lineId') {
            return index + 1;
        }
        if (id === 'description') {
            return wrapObjectToText(invoiceLinesHolder[index].description, index, 'description');
        }
        if (id === 'vatCategory') {
            return wrapObjectToText(invoiceLinesHolder[index].vatCategory, index, 'vatCategory');
        }
        if (id === 'serviceCategory') {
            return wrapObjectToText(
                invoiceLinesHolder[index].serviceCategory,
                index,
                'serviceCategory',
            );
        }
        if (id === 'codeGtin') {
            return invoiceLinesHolder[index].combinedCodes &&
                invoiceLinesHolder[index].combinedCodes.length ? (
                invoiceLinesHolder[index].combinedCodes.length === 1 ? (
                    <TextWrapper>
                        <RowText>{invoiceLinesHolder[index].combinedCodes[0]}</RowText>
                        <RowText>{invoiceLinesHolder[index].gtin}</RowText>
                    </TextWrapper>
                ) : (
                    <GroupByGtinButton index={index} gtin={invoiceLinesHolder[index].gtin} />
                )
            ) : isMarkedItem ? (
                <TextWrapper>
                    <RowText>{invoiceLinesHolder[index].code}</RowText>
                    <RowText>{invoiceLinesHolder[index].gtin}</RowText>
                </TextWrapper>
            ) : (
                <TextWrapper>
                    <RowText>{t('unmarked')}</RowText>
                </TextWrapper>
            );
        }
        return value !== '' ? (
            value
        ) : id === 'vat' ? (
            '-'
        ) : (
            <Input
                id={`${id}_${index}`}
                size="sm"
                autoFocus={isEditing}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                disabled={id === 'lines' || id === 'lineAmountWVat'}
                placeholder={id === 'lines' || id === 'lineAmountWVat' ? 'Auto' : 'Enter value'}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        onBlur();
                    }
                }}
            />
        );
    }, [index, id, initialValue, value, invoiceLinesHolder[index]]);

    const fullUneditableFields = useMemo(() => {
        const uneditableFields = [
            'codeGtin',
            'lineId',
            'lines',
            'lineAmountWVat',
            'serviceCategory',
            !isStandartCategory(vatCategory.value) && 'vat',
        ];
        const uneditableFieldsForMarkedItem = ['description', 'qty'];

        return isMarkedItem
            ? [...uneditableFields, ...uneditableFieldsForMarkedItem]
            : uneditableFields;
    }, [invoiceLinesHolder, index]);

    if (id === 'vatCategory' && !vatCategoryValue?.value) {
        return (
            <Select
                id={'vatCategorySelect_1'}
                size="sm"
                value={''}
                options={VAT_CATEGORY}
                onChange={onSelectChange}
                menuPosition={'fixed'}
            />
        );
    }

    if (id === 'control') {
        return (
            <LineControlComponent
                fieldsLength={invoiceLinesHolder.length}
                onCopyData={() => copyInvoiceLineData({index})}
                onDuplicateDown={() => {
                    setModalDuplicateLinesModal({index, show: true});
                }}
                onRemoveField={() => removeInvoiceLinesHolder(index)}
                allowToCopyData={copyActions?.allowToCopy ?? false}
                allowToDuplicateDown={copyActions?.allowToDuplicateDown ?? false}
            />
        );
    }

    if (!doesInvoiceExist && isEditing) {
        if (id === 'vatCategory') {
            return (
                <Select
                    id={'vatCategorySelect'}
                    size="sm"
                    value={vatCategoryValue}
                    options={VAT_CATEGORY}
                    onChange={onSelectChange}
                    menuPosition={'fixed'}
                    onBlur={() => {
                        setIsEditing(false);
                    }}
                    autoFocus={isEditing}
                />
            );
        }
        return (
            <Input
                size="sm"
                autoFocus={isEditing}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={''}
                {...(id !== 'description' && {type: 'number', step: '0.01'})}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        onBlur();
                    }
                }}
            />
        );
    }
    return (
        <>
            {id === 'charges' ? (
                doesInvoiceExist ? (
                    invoiceLinesHolder[index]['charges'] ? (
                        <TextWrapperRow>
                            <TextWrapperItem>
                                {invoiceLinesHolder[index]['charges']?.isCharge === 'false'
                                    ? '-'
                                    : '+'}
                            </TextWrapperItem>
                            <TextWrapperItem>
                                {Number(invoiceLinesHolder[index]['charges']?.amountXaf).toFixed(1)}
                            </TextWrapperItem>
                            {invoiceLinesHolder[index]['charges']?.discountRateAndBaseAmount && (
                                <TextWrapperItem withSeparator={true}>
                                    {`${invoiceLinesHolder[index]['charges']?.ratePercent}%`}
                                </TextWrapperItem>
                            )}
                            {checkLineInfo(() => {
                                setInlineModalFormReadonly({
                                    index,
                                    show: true,
                                });
                            })}
                        </TextWrapperRow>
                    ) : (
                        <div style={{padding: '8px 16px'}}>-</div>
                    )
                ) : (
                    <ModalHandler index={index} />
                )
            ) : (
                <DefaultCell
                    noPadding={noPadding}
                    fullUneditableFields={fullUneditableFields as string[]}
                    id={id}
                    allowEditing={!doesInvoiceExist}
                    role="button"
                    tabIndex={index}
                    onClick={() => {
                        if (doesInvoiceExist) {
                            return;
                        }
                        // const uneditableFields = [
                        //     'codeGtin',
                        //     'lineId',
                        //     'lines',
                        //     'lineAmountWVat',
                        //     'serviceCategory',
                        // ];
                        // const uneditableFieldsForMarkedItem = ['description', 'qty'];

                        // const currentItem = invoiceLinesHolder[index];
                        // const {isMarkedItem} = currentItem;

                        // const fullUneditableFields = isMarkedItem
                        //     ? [...uneditableFields, ...uneditableFieldsForMarkedItem]
                        //     : uneditableFields;

                        if (!fullUneditableFields.includes(id)) {
                            setIsEditing(true);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            onBlur();
                        }
                    }}
                >
                    {dataToDisaply}
                </DefaultCell>
            )}
        </>
    );
};

export const SimpleTable = ({
    columns,
    data,
    onPaginationReady,
    cellContainerSettings,
    tableViewExtraSettings = {},
    hideHeaders = false,
    updateMyData,
    skipPageReset,
    editableCell = true,
    initialState = {pageIndex: 0, pageSize: 10},
    emptyDataComponent,
}: SimpleTableProps) => {
    const defaultColumn = {
        Cell: EditableCell,
    };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
            defaultColumn: editableCell ? defaultColumn : undefined,
            initialState,
            autoResetPage: !skipPageReset,
            updateMyData,
        },
        useFlexLayout,
        usePagination,
    );

    useEffect(() => {
        if (onPaginationReady) {
            onPaginationReady({
                pageIndex,
                pageOptions,
                canPreviousPage,
                canNextPage,
                pageCount,
                gotoPage,
                nextPage,
                previousPage,
                setPageSize,
                pageSize,
            });
        }
    }, [
        pageIndex,
        pageOptions,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        pageSize,
        onPaginationReady,
    ]);

    return (
        <Root>
            <TableView tableViewExtraSettings={tableViewExtraSettings} hoverableRow={false}>
                <div {...getTableProps()} className="table">
                    {hideHeaders || (
                        <div className="thead">
                            {headerGroups.map((headerGroup) => (
                                <div {...headerGroup.getHeaderGroupProps()} className="tr">
                                    {headerGroup.headers.map((column: any) => (
                                        <HeaderCell {...column.getHeaderProps()} className="th">
                                            {column.render('Header')}
                                        </HeaderCell>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                    <div {...getTableBodyProps()} className="tbody">
                        {page.map((row: any) => {
                            prepareRow(row);
                            return (
                                <div {...row.getRowProps()} className="tr" role="row">
                                    {row.cells.map((cell: any) => (
                                        <div {...cell.getCellProps()} className="td">
                                            <CellContainer
                                                cellContainerSettings={cellContainerSettings}
                                            >
                                                {cell.render('Cell')}
                                            </CellContainer>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                    {emptyDataComponent && page.length === 0 && emptyDataComponent}
                </div>
            </TableView>
        </Root>
    );
};

export const SimpleTablePagination = ({
    pageIndex,
    pageOptions,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageSize,
}: PaginationControlsProps) => (
    <PaginationContainer>
        <PaginationControls>
            {/* <PaginationButton type="button" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                {'<<'}
            </PaginationButton> */}
            <IconButton
                icon={<Icon source={LeftIcon} width={8} height={13} />}
                disabled={!canPreviousPage}
                color={!canPreviousPage ? colors.textMuted : colors.textPrimary}
                onClick={() => previousPage()}
                hoverIndication
            />
            <PageInfo>
                Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
            </PageInfo>
            <IconButton
                icon={<Icon source={RightIcon} width={8} height={13} />}
                disabled={!canNextPage}
                color={!canNextPage ? colors.textMuted : colors.textPrimary}
                onClick={() => nextPage()}
                hoverIndication
            />
            {/* <PaginationButton
                type="button"
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
            >
                {'>>'}
            </PaginationButton> */}
            {/* <PageSizeSelect
                value={pageSize}
                onChange={(e) => {
                    setPageSize(Number(e.target.value));
                }}
            >
                {[10, 20, 30, 40, 50].map((size) => (
                    <option key={size} value={size}>
                        Показать {size}
                    </option>
                ))}
            </PageSizeSelect> */}
        </PaginationControls>
    </PaginationContainer>
);
